import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, tvShows } from '@/mocks/tvShows';
import ShowCard from '@/components/feature/ShowCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];

  let filteredShows = activeCategory === 'all'
    ? tvShows
    : tvShows.filter((s) => s.categorySlug === activeCategory);

  if (sortBy === 'rating') {
    filteredShows = [...filteredShows].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'year') {
    filteredShows = [...filteredShows].sort((a, b) => b.year - a.year);
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 天堂影视`,
    description: `天堂影视${currentCategory.name}专区，高清免费在线观看，不缓冲不卡顿，海量${currentCategory.name}资源尽在天堂影视。`,
    url: `https://www.tiantangyingshi.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '天堂影视',
      url: 'https://www.tiantangyingshi.com',
    },
  };

  const categoryTitle = activeCategory === 'all' ? '全部剧集' : currentCategory.name;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <article>
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryTitle },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-3 font-heading">
                {categoryTitle}
              </h1>
              <p className="text-sm text-foreground-400 mt-2">
                {activeCategory === 'all'
                  ? '浏览全部电视剧资源，海量好剧随心看'
                  : `天堂影视${currentCategory.name}专区 · 共${currentCategory.count}部 · 高清免费在线观看`}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs w-3.5 h-3.5 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between">
                <p className="text-sm text-foreground-400">
                  共找到 <strong className="text-foreground-700">{filteredShows.length}</strong> 部剧集
                </p>
                <div className="flex items-center gap-1">
                  {[
                    { value: 'default', label: '默认排序' },
                    { value: 'rating', label: '评分最高' },
                    { value: 'year', label: '最新上线' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value as 'rating' | 'year' | 'default')}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        sortBy === opt.value
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-500 hover:bg-background-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 pb-12 md:pb-16">
            <div className="max-w-7xl mx-auto">
              {filteredShows.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {filteredShows.map((show) => (
                    <div key={show.id} data-product-shop="">
                      <ShowCard show={show} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的剧集</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}